import {useMemo, useRef} from 'react';
import {shallowEqual} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {createSelector} from 'reselect';

import {normalizeValue} from '../../components/PanelTitle/getTitleFromConfig';
import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {configSearchTitles, searchRegexFromQuery} from '../../util/panelbank';
import {isCustomPanelKey} from '../../util/panels';
import {useSelector} from '../hooks';
import {usePanelSearchDebouncedContext} from '../panelbank/PanelSearchContextProvider';
import {makePartSelector} from '../views/selectors';
import {useSampledHistoryKeys} from './panelGenHistoryKeysStore';

const NO_METRICS: string[] = [];

export function getRequiredMetrics(locationSearch: string): string[] {
  const requiredMetrics = [];

  // Full screen panels sometimes use display name for lookup. This is always
  // true for auto-generated panels where there's no persisted panel ID.
  // In this case, the panel display name will be the panel key/metric name.
  // If the key isn't included in the truncated key set, the panel may incorrectly
  // show as not found. We need to pass it to SAHK as a required metric to ensure
  // it's included in the result set if it exists.
  const fullScreenPanelName =
    new URLSearchParams(locationSearch).get('panelDisplayName') ?? '';
  const normalizedPanelName = normalizeValue(fullScreenPanelName).trim();
  if (normalizedPanelName) {
    requiredMetrics.push(normalizedPanelName);
  }

  return requiredMetrics;
}

type ConfigLike = {
  metrics?: readonly unknown[] | null;
  /** Media panels keep their keys here rather than in `metrics`. */
  mediaKeys?: readonly unknown[] | null;
  metricRegex?: unknown;
  useMetricRegex?: boolean;
};

function getStringMetrics(
  metrics: readonly unknown[] | null | undefined
): string[] {
  if (!Array.isArray(metrics)) {
    return [];
  }
  return metrics.filter(
    (metric): metric is string => typeof metric === 'string' && metric !== ''
  );
}

/**
 * The keys a panel config lists, whichever field it keeps them in. Line plots
 * use `metrics`, media panels use `mediaKeys`; a panel has one or the other, so
 * reading both is safe and keeps the two panel types symmetric.
 *
 * These are the keys the config names, not necessarily the ones on screen: a
 * regex panel's `metrics` are superseded by whatever `metricRegex` resolves to.
 */
function getStringConfigKeys(config: ConfigLike | undefined): string[] {
  if (config == null) {
    return [];
  }
  return [
    ...getStringMetrics(config.metrics),
    ...getStringMetrics(config.mediaKeys),
  ];
}

/**
 * One override's key and config, plus the two values derived from them that
 * both the search filter and the pinning step need. Computed once per override
 * so neither `isCustomPanelKey` nor `getStringConfigKeys` runs twice.
 */
type OverrideParts = {
  key: string;
  config: ConfigLike | undefined;
  /** Whether `key` identifies a custom (isAuto === false) panel. */
  isCustom: boolean;
  /** The keys the config lists explicitly: `config.metrics` + `config.mediaKeys`. */
  configKeys: string[];
};

/**
 * The strings the active search is tested against to decide whether this
 * override's panel gets pinned. Mirrors what the display filter matches on
 * (panelSearchStrings: the panel's metrics + title) so the two agree — if a
 * search would show the panel, we also pin its key. To that we add the identity
 * key and, for regex panels, the regex pattern, so a match on the title or the
 * regex alone still pins the panel.
 *
 * Deliberately a superset of what the override pins: titles and the regex
 * pattern are matchable but are not metric names, and a regex panel's
 * `config.metrics` are matchable but resolve dynamically. Don't feed the result
 * into the pinned-metrics set.
 *
 * A Set rather than an array: an edited auto panel's key, sole metric and
 * chartTitle are frequently the same string, and deduping them means the search
 * regex is tested once instead of three times.
 */
function overrideSearchStrings({
  key,
  config,
  isCustom,
  configKeys,
}: OverrideParts): Set<string> {
  const strings = new Set<string>();
  if (!isCustom) {
    strings.add(key);
  }
  if (config != null) {
    for (const configKey of configKeys) {
      strings.add(configKey);
    }
    for (const title of configSearchTitles(config)) {
      strings.add(title);
    }
    if (
      config.useMetricRegex === true &&
      typeof config.metricRegex === 'string'
    ) {
      strings.add(config.metricRegex);
    }
  }
  return strings;
}

/**
 * Whether the active search matches this override's panel, and so whether the
 * override's metrics get pinned. The override-side counterpart to
 * `searchRegexMatchesPanel`; Set has no `.some`, so this is the same early-out
 * scan written as a loop.
 */
function searchRegexMatchesOverride(
  regex: RegExp,
  parts: OverrideParts
): boolean {
  for (const searchString of overrideSearchStrings(parts)) {
    if (regex.test(searchString)) {
      return true;
    }
  }
  return false;
}

/**
 * Metrics to pin from a workspace's config + placement overrides. Exported for
 * tests. Returns the override keys (identity metrics). Placement overrides
 * contribute their key for symmetry: a panel that was only moved (not
 * config-edited) can be orphaned the same way.
 *
 * The override key is the panel's identity metric — an edited auto panel is
 * regenerated from it on reload and the override re-attaches by it, so pinning it
 * keeps the panel from being orphaned when NGW's search-scoped/capped sample
 * would otherwise drop it.
 *
 * When `searchQuery` is a non-empty search, only overrides whose panel matches
 * it are pinned, so the capped sample's limited pin budget is spent on the
 * panels being searched for rather than every edited panel in the workspace.
 */
export function getOverriddenPanelMetricKeys(
  panelConfigOverrides: Record<string, {config?: unknown}> | undefined,
  panelPlacementOverrides: Record<string, unknown> | undefined,
  searchQuery?: string
): string[] {
  const searchRegex =
    searchQuery != null && searchQuery.trim() !== ''
      ? searchRegexFromQuery(searchQuery)
      : null;
  const metrics = new Set<string>();

  for (const [key, override] of Object.entries(panelConfigOverrides ?? {})) {
    const config = (override?.config ?? undefined) as ConfigLike | undefined;
    const parts: OverrideParts = {
      key,
      config,
      isCustom: isCustomPanelKey(key),
      configKeys: getStringConfigKeys(config),
    };
    // When a search is active, drop this override unless its panel matches the
    // search — this is what keeps requiredMetrics scoped to the panels being
    // searched for, instead of pinning every edited panel and burning the capped
    // sample's limited pin budget on ones the user isn't looking for.
    if (
      searchRegex != null &&
      !searchRegexMatchesOverride(searchRegex, parts)
    ) {
      continue;
    }
    if (!parts.isCustom) {
      metrics.add(key);
    }
  }

  for (const key of Object.keys(panelPlacementOverrides ?? {})) {
    if (isCustomPanelKey(key)) {
      continue;
    }
    // Placement-only overrides have no config to title-match against; fall back
    // to the key, which is the panel's auto-generated title/metric.
    if (searchRegex != null && !searchRegex.test(key)) {
      continue;
    }
    metrics.add(key);
  }

  return [...metrics].sort();
}

/**
 * The metrics that edited auto panels are configured to show: every non-regex
 * config override's `config.metrics` and `config.mediaKeys`, de-duped and sorted.
 *
 * These are the keys `haveDataForPanel` and the media card-type lookup consult
 * for an edited panel, and they are not pinned into the PanelGenHistoryKeys sample
 * (see `getOverriddenPanelMetricKeys`), so `usePanelVisibilityKeysSync` looks
 * them up separately. Regex overrides contribute nothing since they are
 * force-rendered and dynamically resolve their own metrics.
 */
export function getOverriddenPanelDisplayedMetrics(
  panelConfigOverrides: Record<string, {config?: unknown}> | undefined
): string[] {
  const metrics = new Set<string>();
  for (const [key, override] of Object.entries(panelConfigOverrides ?? {})) {
    if (isCustomPanelKey(key)) {
      continue;
    }
    const config = (override?.config ?? undefined) as ConfigLike | undefined;
    if (config?.useMetricRegex === true) {
      continue;
    }
    for (const metric of getStringConfigKeys(config)) {
      if (metric !== key) {
        metrics.add(metric);
      }
    }
  }
  return [...metrics].sort();
}

/**
 * Metric keys required to keep edited-auto panels present in NGW's panel-gen
 * sample: override identity metrics, filtered to the active search.
 * Custom (isAuto === false) panels are intentionally omitted
 * — they are persisted and force-rendered, so they never need pinning.
 */
function usePanelMetricKeys(): string[] {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const selector = createSelector(
    makePartSelector(panelBankConfigRef),
    panelBankConfig => ({
      panelConfigOverrides: panelBankConfig.panelConfigOverrides,
      panelPlacementOverrides: panelBankConfig.panelPlacementOverrides,
    })
  );
  const {panelConfigOverrides, panelPlacementOverrides} = useSelector(
    selector,
    shallowEqual
  );
  const {networkDebouncedSearchQuery} = usePanelSearchDebouncedContext();
  return getOverriddenPanelMetricKeys(
    panelConfigOverrides,
    panelPlacementOverrides,
    networkDebouncedSearchQuery
  );
}

/**
 * All metrics to send to SAHK as `requiredMetrics`, so panels that must survive
 * the truncated/search-scoped sample aren't dropped: the full-screen panel name
 * (from the URL, only when it isn't already sampled -- see below) plus
 * edited-auto panels' metrics matching the active search.
 *
 * `requiredMetrics` is a SAHK query variable, so every change to it refetches
 * and hands the panel-bank auto-sync effect a new key set, which regenerates
 * the whole panel bank synchronously. Entering full screen and leaving it again
 * therefore cost two regenerations, and the one on the way out is what blocks
 * the click back to the workspace.
 *
 * Expanding a panel the user can see means its metric is already in the sample,
 * so pinning it buys nothing and both regenerations are pure waste. Only pin
 * when the metric is genuinely absent -- a deep link straight to a full-screen
 * URL for a metric truncation dropped.
 *
 * The decision is latched per metric, and only ever flips towards pinning:
 * re-deciding "not needed" after a pinned metric lands in the sample would
 * unpin it, let the next sample evict it, and pin it again. Staying open to
 * false -> true means a metric evicted later still gets pinned on the next
 * render, so the panel self-heals instead of showing as not found.
 */
export function useRequiredMetrics(): string[] {
  const location = useLocation();
  const fullScreenMetrics = useMemo(
    () => getRequiredMetrics(location.search),
    [location.search]
  );
  const sampledHistoryKeys = useSampledHistoryKeys();
  const panelMetrics = usePanelMetricKeys();

  const fullScreenMetric = fullScreenMetrics[0] ?? '';
  const pinRef = useRef<{metric: string; isPinned: boolean}>({
    metric: '',
    isPinned: false,
  });
  // oxlint-disable-next-line react/refs
  if (pinRef.current.metric !== fullScreenMetric) {
    // oxlint-disable-next-line react/refs
    pinRef.current = {metric: fullScreenMetric, isPinned: false};
  }
  if (
    fullScreenMetric !== '' &&
    // oxlint-disable-next-line react/refs
    !pinRef.current.isPinned &&
    sampledHistoryKeys.keys[fullScreenMetric] == null
  ) {
    // oxlint-disable-next-line react/refs
    pinRef.current = {metric: fullScreenMetric, isPinned: true};
  }
  // oxlint-disable-next-line react/refs
  const pinnedFullScreenMetrics = pinRef.current.isPinned
    ? fullScreenMetrics
    : NO_METRICS;

  return useMemo(
    () => Array.from(new Set([...pinnedFullScreenMetrics, ...panelMetrics])),
    [pinnedFullScreenMetrics, panelMetrics]
  );
}
